import { cn } from 'cn';
import { Fragment, type ReactElement, useState } from 'react';

import { type UUID } from '~/types/common';

import { type Item } from '~/ui/molecules/MultiItemsManager/types';

import { Chip } from './Chip';

type P = {
  readonly clickable?: boolean;
  readonly deletable?: boolean;
  readonly fieldName?: 'id' | 'name';
  readonly items: Array<{ id: UUID; name: string; selected?: boolean }>;
  readonly onClick?: (item: Item) => void;
  readonly onDelete?: (item: UUID | undefined) => void;
  readonly onPaginationClick?: () => void;
  readonly renderItem?: (item: Item) => ReactElement | undefined;
  readonly selectedItemIsNotClickable?: boolean;
  readonly withInternalPagination?: boolean;
} & ComponentStyling;

export const ChipList = ({
  className,
  clickable,
  deletable,
  fieldName,
  items,
  onClick,
  onDelete,
  onPaginationClick: parentOnPaginationClick,
  renderItem,
  selectedItemIsNotClickable,
  withInternalPagination,
}: P) => {
  const INTERNAL_PAGINATE_BY = 10;

  // Internal Pagination can be toggled via withInternalPagination.
  // The internal pagination helps to not have to implement the pagination logic in the parent component.
  const [internalPaginationOffset, setInternalPaginationOffset] =
    useState(INTERNAL_PAGINATE_BY);

  const onPaginationClick = () => {
    if (!withInternalPagination) {
      parentOnPaginationClick?.();
      return;
    }

    setInternalPaginationOffset(
      internalPaginationOffset + INTERNAL_PAGINATE_BY,
    );
  };

  const getItems = () => {
    if (!withInternalPagination) {
      return items;
    }

    return items.slice(0, internalPaginationOffset);
  };

  return (
    <div className={cn('flex flex-wrap items-center gap-1', className)}>
      {getItems().map((item) => (
        <Fragment key={item.id}>
          {renderItem?.(item) ?? (
            <Chip
              {...item}
              clickable={
                selectedItemIsNotClickable && item.selected ? false : clickable
              }
              deletable={deletable}
              name={item[fieldName ?? 'name']}
              onClick={() => onClick?.(item)}
              onDelete={onDelete}
              onPaginationClick={onPaginationClick}
            />
          )}
        </Fragment>
      ))}
      {withInternalPagination && items.length > internalPaginationOffset ? (
        <Chip
          key="pagination-key"
          id={undefined}
          name={`+ ${items.length - internalPaginationOffset} weitere Items`}
          isPaginationChip
          onPaginationClick={onPaginationClick}
        />
      ) : null}
    </div>
  );
};
